import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Settings2, CheckCircle2, XCircle, AlertTriangle, MinusCircle, History, FlaskConical, Pencil, Eye, Plus, Search, Trash2, Radio, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUS = {
  configured: { c: "text-[#22c55e]", Icon: CheckCircle2, label: "Configured" },
  missing: { c: "text-[#ef4444]", Icon: XCircle, label: "Missing" },
  disabled: { c: "text-[#A1A1AA]", Icon: MinusCircle, label: "Disabled" },
  draft: { c: "text-[#f59e0b]", Icon: AlertTriangle, label: "Draft change" },
  invalid: { c: "text-[#ef4444]", Icon: XCircle, label: "Invalid" },
};
const fmtDate = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleString() : "—");
const errText = (e) => e?.response?.data?.detail || e?.message || "Action failed";

function ConfirmDialog({ open, word, title, body, onConfirm, onClose, busy }) {
  const [txt, setTxt] = useState("");
  useEffect(() => { if (open) setTxt(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-[#111] text-white">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">{body} Type <span className="font-mono font-bold text-white">{word}</span> to confirm.</DialogDescription>
        <input autoFocus data-testid="cfg-confirm-input" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={word}
          className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
          <button data-testid="cfg-confirm-btn" disabled={txt !== word || busy} onClick={() => onConfirm(txt)}
            className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />} {word}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditControl({ item, value, onChange }) {
  if (item.type === "boolean") {
    return <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`cfg-input-${item.key}`} checked={value === true || value === "true"} onChange={(e) => onChange(e.target.checked)} /> Enabled</label>;
  }
  if (item.type === "select") {
    return <select data-testid={`cfg-input-${item.key}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
      <option value="" disabled>Choose…</option>
      {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>;
  }
  if (item.type === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return <div data-testid={`cfg-input-${item.key}`} className="flex flex-wrap gap-2">
      {(item.options || []).map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${arr.includes(o) ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>{o}</button>
      ))}
    </div>;
  }
  if (item.type === "number") {
    return <input type="number" data-testid={`cfg-input-${item.key}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />;
  }
  if (item.type === "text" || item.type === "json") {
    return <textarea data-testid={`cfg-input-${item.key}`} rows={item.type === "json" ? 5 : 4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={item.type === "json" ? '{ "example": true }' : ""} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm outline-none focus:border-[#00F0FF]/50" />;
  }
  if (item.type === "url") {
    return <input type="url" data-testid={`cfg-input-${item.key}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />;
  }
  return <input type={item.is_secret ? "password" : "text"} data-testid={`cfg-input-${item.key}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={item.is_secret ? "Enter new secret value" : ""} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" autoComplete="new-password" />;
}

function displayValue(item) {
  if (item.is_secret) return item.masked || "not set";
  const v = item.value;
  if (v === null || v === undefined) return "not set";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function ConfigItem({ item, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [versions, setVersions] = useState(null);
  const st = STATUS[item.config_status] || STATUS.missing;

  const startEdit = () => {
    if (item.is_secret) setVal("");
    else if (item.type === "multi_select") setVal(Array.isArray(item.draft_value ?? item.value) ? (item.draft_value ?? item.value) : []);
    else if (item.type === "json") { const cur = item.draft_value ?? item.value; setVal(typeof cur === "string" ? cur : JSON.stringify(cur ?? "", null, 2)); }
    else setVal(item.draft_value ?? item.value ?? "");
    setEditing(true);
  };
  const saveDraft = async () => {
    setBusy(true);
    try { await api.adminConfigSaveDraft(item.key, val, item.rev); toast.success("Draft saved"); setEditing(false); onRefresh(); }
    catch (e) { toast.error(errText(e)); if (e?.response?.status === 409) onRefresh(); }
    setBusy(false);
  };
  const test = async () => {
    try { const r = await api.adminConfigTest(item.key); r.ok ? toast.success(r.message) : toast.error(r.message); }
    catch (e) { toast.error(errText(e)); }
  };
  const openVersions = async () => { try { setVersions((await api.adminConfigVersions(item.key)).versions); } catch { setVersions([]); } };
  const del = () => setConfirm({ word: "DELETE", title: `Delete field ${item.key}`, body: "This removes the configuration field and its history. The application will fall back to the environment/default.", run: async () => { await api.adminConfigDeleteField(item.key); toast.success("Field deleted"); } });

  const doPublish = () => setConfirm({ word: "PUBLISH", title: `Publish ${item.label}`, body: "The live value will change immediately across the app.", run: async (c) => { await api.adminConfigPublish(item.key, c, item.rev); toast.success("Configuration updated successfully."); } });
  const doDisable = () => setConfirm({ word: "DISABLE", title: `Disable ${item.label}`, body: "The app will fall back to the environment/default value.", run: async (c) => { await api.adminConfigDisable(item.key, c); toast.success("Disabled"); } });
  const doRollback = (v) => setConfirm({ word: "ROLLBACK", title: `Rollback ${item.label} to v${v}`, body: "This restores that stored version as the live value.", run: async (c) => { await api.adminConfigRollback(item.key, v, c); toast.success("Rolled back"); setVersions(null); } });

  const runConfirm = async (c) => {
    setBusy(true);
    try { await confirm.run(c); setConfirm(null); onRefresh(); }
    catch (e) { toast.error(errText(e)); if (e?.response?.status === 409) { setConfirm(null); onRefresh(); } }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`cfg-item-${item.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {item.is_secret ? <Lock size={14} className="text-[#f59e0b]" /> : <Settings2 size={14} className="text-[#00F0FF]" />}
            <span className="font-semibold text-white">{item.label}</span>
            <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-[#A1A1AA]">{item.key}</span>
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-[#A1A1AA]">{item.is_secret ? "secret" : item.type}</span>
            <span className={`flex items-center gap-1 text-xs font-semibold ${st.c}`} data-testid={`cfg-status-${item.key}`}><st.Icon size={12} /> {st.label}</span>
            {!item.code_backed && <span className="rounded bg-[#f59e0b]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#f59e0b]" data-testid={`cfg-unused-${item.key}`} title="Stored & served, but no application code reads this key yet.">Stored — not yet used by code</span>}
          </div>
          {item.description && <div className="mt-0.5 text-xs text-[#A1A1AA]">{item.description}</div>}
          <div className="mt-0.5 font-mono text-sm text-white/70" data-testid={`cfg-value-${item.key}`}>
            <span>{displayValue(item)}</span>
            {"  "}<span className="ml-2 text-[10px] uppercase text-[#A1A1AA]">· from {item.source}</span>
          </div>
          {item.has_draft && <div className="mt-0.5 text-xs text-[#f59e0b]" data-testid={`cfg-draft-${item.key}`}>Draft: {item.is_secret ? (item.draft_masked || "•••") : (Array.isArray(item.draft_value) ? item.draft_value.join(", ") : String(item.draft_value))} — not live until published</div>}
          <div className="mt-1 text-[11px] text-[#A1A1AA]">Updated {fmtDate(item.updated_at)}{item.updated_by ? ` by ${item.updated_by}` : ""} · v{item.version_no}</div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button data-testid={`cfg-edit-${item.key}`} onClick={startEdit} className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Pencil size={12} /> {item.is_secret ? "Replace" : "Edit"}</button>
          {item.has_draft && <button data-testid={`cfg-publish-${item.key}`} onClick={doPublish} className="rounded-lg bg-[#00F0FF] px-2.5 py-1.5 text-xs font-bold text-black">Publish</button>}
          <button data-testid={`cfg-test-${item.key}`} onClick={test} className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><FlaskConical size={12} /> Test</button>
          {item.status === "active"
            ? <button data-testid={`cfg-disable-${item.key}`} onClick={doDisable} className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#f59e0b] hover:bg-white/5">Disable</button>
            : <button data-testid={`cfg-enable-${item.key}`} onClick={async () => { try { await api.adminConfigEnable(item.key); toast.success("Enabled"); onRefresh(); } catch (e) { toast.error(errText(e)); } }} className="rounded-lg border border-[#22c55e]/40 px-2.5 py-1.5 text-xs font-semibold text-[#22c55e] hover:bg-white/5">Enable</button>}
          <button data-testid={`cfg-history-${item.key}`} onClick={openVersions} className="rounded-lg border border-white/15 p-1.5 text-[#A1A1AA] hover:text-white"><History size={13} /></button>
          {!item.code_backed && <button data-testid={`cfg-delete-${item.key}`} onClick={del} className="rounded-lg border border-[#ef4444]/40 p-1.5 text-[#ef4444] hover:bg-white/5"><Trash2 size={13} /></button>}
        </div>
      </div>

      {editing && (
        <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`cfg-editor-${item.key}`}>
          <div className="mb-1 text-[11px] uppercase text-[#A1A1AA]">New value{item.is_secret ? " (write-only, encrypted at rest)" : ""}</div>
          <EditControl item={item} value={val} onChange={setVal} />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
            <button data-testid={`cfg-savedraft-${item.key}`} disabled={busy} onClick={saveDraft} className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{busy && <Loader2 size={12} className="animate-spin" />} Save Draft</button>
          </div>
        </div>
      )}

      {versions !== null && (
        <Dialog open onOpenChange={() => setVersions(null)}>
          <DialogContent className="max-w-md border-white/10 bg-[#111] text-white">
            <DialogTitle>{item.label} — version history</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Historical secret values are never shown. Rollback restores the encrypted stored version.</DialogDescription>
            <div className="mt-2 max-h-[300px] space-y-2 overflow-y-auto">
              {versions.length === 0 ? <div className="text-sm text-[#A1A1AA]">No published versions yet.</div> :
                versions.map((v) => (
                  <div key={v.version_no} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2 text-sm">
                    <div><span className="font-semibold">v{v.version_no}</span> {v.is_current && <span className="ml-1 text-[10px] text-[#22c55e]">CURRENT</span>}<div className="text-[11px] text-[#A1A1AA]">{fmtDate(v.published_at)} · {v.created_by}</div></div>
                    {!v.is_current && <button data-testid={`cfg-rollback-${item.key}-${v.version_no}`} onClick={() => doRollback(v.version_no)} className="rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-[#00F0FF] hover:bg-white/5">Rollback</button>}
                  </div>
                ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog open={!!confirm} word={confirm?.word} title={confirm?.title} body={confirm?.body} busy={busy} onClose={() => setConfirm(null)} onConfirm={runConfirm} />
    </div>
  );
}

const KEY_RE = /^[A-Z][A-Z0-9_]{1,63}$/;

function AddFieldModal({ open, onClose, forceSecret, categories, types, onSaved }) {
  const empty = { key: "", label: "", type: forceSecret ? "secret" : "text", category: "General", description: "", options: "", default: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...empty, type: forceSecret ? "secret" : "text" }); }, [open, forceSecret]); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const needsOptions = f.type === "select" || f.type === "multi_select";
  const save = async () => {
    if (!KEY_RE.test(f.key)) { toast.error("Key must be UPPER_SNAKE_CASE (e.g. TRANSCRIPTION_ENGINE), 2–64 chars."); return; }
    setBusy(true);
    try {
      const spec = { key: f.key.trim(), label: f.label.trim() || f.key.trim(), type: f.type, category: f.category, description: f.description.trim() };
      if (needsOptions) spec.options = f.options.split(",").map((s) => s.trim()).filter(Boolean);
      if (f.default !== "") spec.default = f.type === "boolean" ? (f.default === "true" || f.default === true) : f.default;
      await api.adminConfigAddField(spec);
      toast.success("Configuration field created");
      onSaved(); onClose();
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-[#111] text-white" data-testid="add-field-modal">
        <DialogTitle>{forceSecret ? "Add Secret" : "Add Configuration"}</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">The <b>Key</b> must exactly match the key your application code reads. Values only take effect once code uses that key.</DialogDescription>
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Display Name</label>
              <input data-testid="add-field-label" value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="Transcription Engine" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Key</label>
              <input data-testid="add-field-key" value={f.key} onChange={(e) => set("key", e.target.value.toUpperCase())} placeholder="TRANSCRIPTION_ENGINE" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm outline-none focus:border-[#00F0FF]/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Type</label>
              <select data-testid="add-field-type" disabled={forceSecret} value={f.type} onChange={(e) => set("type", e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white disabled:opacity-60">
                {(types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Category</label>
              <select data-testid="add-field-category" value={f.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
                {(categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {needsOptions && (
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Allowed values (comma-separated)</label>
              <input data-testid="add-field-options" value={f.options} onChange={(e) => set("options", e.target.value)} placeholder="provider_a, provider_b, provider_c" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Description</label>
            <input data-testid="add-field-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Controls which transcription provider handles new jobs." className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
          </div>
          {!forceSecret && (
            <div>
              <label className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Default value (optional)</label>
              <input data-testid="add-field-default" value={String(f.default)} onChange={(e) => set("default", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
          <button data-testid="add-field-save" disabled={busy} onClick={save} className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TranscriptionPanel({ providers, current, fallback, fallbackEnabled }) {
  const [testing, setTesting] = useState(null);
  const runTest = async (id) => {
    setTesting(id);
    try { const r = await api.adminTranscriptionProviderTest(id); r.ok ? toast.success(r.message) : toast.error(r.message); }
    catch (e) { toast.error(errText(e)); }
    setTesting(null);
  };
  if (!providers?.length) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="transcription-providers">
      <div className="mb-1 flex items-center gap-2 text-sm font-bold text-white"><Radio size={15} className="text-[#00F0FF]" /> Transcription Engines</div>
      <div className="mb-3 text-xs text-[#A1A1AA]">Active engine: <span className="font-mono text-white">{current || "auto"}</span>{fallbackEnabled && fallback && fallback !== "none" ? <> · fallback: <span className="font-mono text-white">{fallback}</span></> : null}. Change it below via <b>Transcription Engine</b>. New jobs use the active engine; running jobs keep theirs.</div>
      <div className="grid gap-2 md:grid-cols-3">
        {providers.map((p) => (
          <div key={p.id} className="rounded-xl border border-white/10 p-3" data-testid={`provider-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-white">{p.display_name}</span>
              <span className={`flex items-center gap-1 text-[11px] font-semibold ${p.configured ? "text-[#22c55e]" : "text-[#ef4444]"}`}>{p.configured ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {p.configured ? "Ready" : "Missing key"}</span>
            </div>
            <div className="mt-1 text-[11px] text-[#A1A1AA]">{p.description}</div>
            {!p.configured && <div className="mt-1 text-[11px] text-[#ef4444]">Needs: {p.missing_keys.join(", ")}</div>}
            {p.id === current && <div className="mt-1 text-[10px] font-bold uppercase text-[#00F0FF]">Active</div>}
            <button data-testid={`provider-test-${p.id}`} onClick={() => runTest(p.id)} disabled={testing === p.id} className="mt-2 flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-50">{testing === p.id ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />} Test Connection</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SecretsConfig() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | config | secret
  const [addOpen, setAddOpen] = useState(null); // null | 'config' | 'secret'
  const load = useCallback(() => { api.adminConfigList().then(setData).catch(() => setData({ items: [], health: {}, categories: [], config_types: [], transcription_providers: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.items.filter((i) => {
      if (filter === "secret" && !i.is_secret) return false;
      if (filter === "config" && i.is_secret) return false;
      if (!term) return true;
      return [i.key, i.label, i.category, i.description].filter(Boolean).some((s) => String(s).toLowerCase().includes(term));
    });
  }, [data, q, filter]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  const byCat = {};
  filtered.forEach((i) => { (byCat[i.category] = byCat[i.category] || []).push(i); });
  const tp = data.transcription_providers || [];
  const engineItem = data.items.find((i) => i.key === "TRANSCRIPTION_ENGINE");
  const fbItem = data.items.find((i) => i.key === "TRANSCRIPTION_FALLBACK_ENGINE");
  const fbEnItem = data.items.find((i) => i.key === "TRANSCRIPTION_FALLBACK_ENABLED");

  return (
    <div className="space-y-5" data-testid="secrets-config">
      <div className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-[#A1A1AA]">
        <Eye size={14} className="mt-0.5 shrink-0" />
        <span>Secrets are encrypted at rest and never sent to the browser — only a masked hint is shown. Changes go live on <b>Publish</b> with no deployment or restart. Environment variables act as fallback. Adding a field stores a value only; <b>the application must already read that key</b> for it to have any effect.</span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input data-testid="cfg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search config (transcription, stripe, gemini, upload…)" className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm outline-none focus:border-[#00F0FF]/50" />
        </div>
        <div className="flex rounded-lg border border-white/10 p-0.5">
          {[["all", "All"], ["config", "Configuration"], ["secret", "Secrets"]].map(([v, l]) => (
            <button key={v} data-testid={`cfg-filter-${v}`} onClick={() => setFilter(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${filter === v ? "bg-[#00F0FF] text-black" : "text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="add-config-btn" onClick={() => setAddOpen("config")} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white hover:bg-white/5"><Plus size={14} /> Add Configuration</button>
        <button data-testid="add-secret-btn" onClick={() => setAddOpen("secret")} className="flex items-center gap-1.5 rounded-lg bg-[#f59e0b] px-3 py-2 text-xs font-bold text-black hover:opacity-90"><Lock size={14} /> Add Secret</button>
      </div>

      <TranscriptionPanel providers={tp} current={engineItem?.value} fallback={fbItem?.value} fallbackEnabled={fbEnItem?.value === true || fbEnItem?.value === "true"} />

      {/* Health dashboard */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="cfg-health">
        <div className="mb-3 text-sm font-bold text-white">Configuration Health</div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(data.health).map(([cat, items]) => (
            <div key={cat} className="rounded-xl border border-white/10 p-3">
              <div className="mb-1.5 text-[11px] uppercase tracking-widest text-[#A1A1AA]">{cat}</div>
              {items.map((x) => { const s = STATUS[x.status] || STATUS.missing; return (
                <div key={x.key} className="flex items-center justify-between py-0.5 text-sm">
                  <span className="truncate text-white/80">{x.label}</span>
                  <span className={`flex items-center gap-1 text-xs ${s.c}`}><s.Icon size={12} /> {s.label}</span>
                </div>); })}
            </div>
          ))}
        </div>
      </div>

      {Object.keys(byCat).length === 0 ? (
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-6 text-sm text-[#A1A1AA]"><Info size={15} /> No configuration matches your search.</div>
      ) : Object.entries(byCat).map(([cat, items]) => (
        <div key={cat} className="space-y-2">
          <div className="text-sm font-bold text-white/90">{cat}</div>
          {items.map((i) => <ConfigItem key={i.key} item={i} onRefresh={load} />)}
        </div>
      ))}

      <AddFieldModal open={!!addOpen} forceSecret={addOpen === "secret"} categories={data.categories} types={(data.config_types || []).filter((t) => t !== "secret")} onClose={() => setAddOpen(null)} onSaved={load} />
    </div>
  );
}
